import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { PreorderExternalStatus, PREORDERS_TABLE_TAB } from '@ekhodealer/ekho-common';
import {
	Button,
	Empty,
	Group,
	Pagination,
	Popup,
	PopupTrigger,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { SortDirection, TableColumn } from '@common/components/Table/Table';
import type { PreordersTableRow } from '@ekhodealer/ekho-common';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import NoOrders from '@common/components/icons/NoOrders';
import { SelectAllBanner } from '@common/components/SelectAllBanner';
import { Table } from '@common/components/Table/Table';
import { PLACEHOLDER } from '@common/lib/strings';

import { useExportPreorders } from '../api/useExportPreorders';
import { useBulkInvitePreordersToCheckout } from '../api/useInvitePreorderToCheckout';
import { useSearchPreorders } from '../api/useSearchPreorders';
import { useBulkUpdatePreorderStatus } from '../api/useUpdatePreorderStatus';
import { PREORDER_DETAILS_TAB } from '../atoms/preorderDetailsDrawerAtoms';
import { activeFiltersCountAtom } from '../atoms/preordersAppliedFiltersAtoms';
import {
	clearAllFiltersAtom,
	currentPageAtom,
	isAllMatchingPreordersSelectedAtom,
	itemsPerPageAtom,
	preordersParamsAtom,
	preorderTabFilterAtom,
	selectedPreordersAtom,
	sortConfigAtom,
	updateCurrentPreordersListAtom,
	updateItemsPerPageAtom,
	updateSelectedPreordersAtom,
	updateSortAtom,
} from '../atoms/preordersAtoms';
import { PREORDERS_TABLE_COLUMN_ID, type PreordersTableColumnId } from '../constants/preordersTableColumns';
import { usePreorderNavigation } from '../hooks/usePreorderNavigation';

import { AccessoriesCell } from './table-cells/AccessoriesCell';
import { BuyerCell } from './table-cells/BuyerCell';
import { DepositAmountCell } from './table-cells/DepositAmountCell';
import { DepositDateCell } from './table-cells/DepositDateCell';
import { EstRetailPriceCell } from './table-cells/EstRetailPriceCell';
import { StateCell } from './table-cells/StateCell';
import { StatusCell } from './table-cells/StatusCell';
import { VehicleCell } from './table-cells/VehicleCell';
import { PREORDER_ACTIONS_CONTEXT, PreorderActions } from './PreorderActions';
import { PreorderDetailsDrawer } from './PreorderDetailsDrawer';

// Centralized cell rendering function
const renderCell = (
	columnId: PreordersTableColumnId,
	item: PreordersTableRow,
	isLoading: boolean,
): React.ReactNode => {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case PREORDERS_TABLE_COLUMN_ID.BUYER:
			return <BuyerCell buyerName={item.buyerFullName || PLACEHOLDER} />;
		case PREORDERS_TABLE_COLUMN_ID.STATUS:
			return <StatusCell status={item.status} />;
		case PREORDERS_TABLE_COLUMN_ID.VEHICLE:
			return (
				<VehicleCell
					vehicleName={item.vehicle?.name || PLACEHOLDER}
					basePrice={item.vehicle?.basePrice}
					imgUrl={item.vehicle?.imgUrl}
				/>
			);
		case PREORDERS_TABLE_COLUMN_ID.ACCESSORIES:
			return <AccessoriesCell accessories={item.accessories ?? []} />;
		case PREORDERS_TABLE_COLUMN_ID.EST_RETAIL_PRICE:
			return <EstRetailPriceCell estimatedRetailPrice={item.vehicle?.estimatedRetailPrice} />;
		case PREORDERS_TABLE_COLUMN_ID.STATE:
			return <StateCell state={item.state} />;
		case PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT:
			return <DepositAmountCell amount={item.preorderAmount} />;
		case PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED:
			return <DepositDateCell date={item.datePurchased} />;
		default:
			return <Text size='bodySmall'>{'\u2014'}</Text>;
	}
};

export const PreordersTable = () => {
	// Get filter params from atoms
	const params = useAtomValue(preordersParamsAtom);
	const currentTab = useAtomValue(preorderTabFilterAtom);

	// Pagination state
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const updateItemsPerPage = useSetAtom(updateItemsPerPageAtom);

	// Selection state
	const selectedPreorders = useAtomValue(selectedPreordersAtom);
	const updateSelectedPreorders = useSetAtom(updateSelectedPreordersAtom);
	const [isAllMatchingSelected, setIsAllMatchingSelected] = useAtom(
		isAllMatchingPreordersSelectedAtom,
	);

	// Sort state
	const sortConfig = useAtomValue(sortConfigAtom);
	const updateSort = useSetAtom(updateSortAtom);

	// Clear all filters
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);
	const hasFiltersApplied = activeFiltersCount > 0;

	// React query hook to get preorders data with filters
	const { data, isLoading } = useSearchPreorders(params);
	const items = useMemo(() => data?.data ?? [], [data?.data]);
	const queryCount = data?.count ?? 0;

	const isAllPageSelected = selectedPreorders === 'all';
	const selectedPreordersCount = isAllMatchingSelected
		? queryCount
		: isAllPageSelected
			? items.length
			: (selectedPreorders as Set<string>).size;

	// Handle exporting
	const [loadingExport, setLoadingExport] = useState(false);
	const exportPreordersMutation = useExportPreorders();

	// Bulk action hooks
	const bulkInviteMutation = useBulkInvitePreordersToCheckout();
	const bulkStatusMutation = useBulkUpdatePreorderStatus();

	// Sync current preorders list for navigation
	const updateCurrentPreordersList = useSetAtom(updateCurrentPreordersListAtom);
	useEffect(() => {
		if (!isLoading && items.length > 0) {
			updateCurrentPreordersList(items.map((item) => item.preorderId));
		}
	}, [items, isLoading, updateCurrentPreordersList]);

	// Helper function to get sort direction for a specific column
	const getSortDirection = (columnId: string): SortDirection => {
		const sort = sortConfig.find((s) => s.column === columnId);
		return sort?.direction ?? 'none';
	};

	// Handle sort changes
	const handleSort = (columnId: string, direction: SortDirection) => {
		updateSort({ columnId, direction });
	};

	// State update handlers
	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		updateItemsPerPage(newItemsPerPage);
	};
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	// Row click opens drawer
	const { openPreorderDetails } = usePreorderNavigation();
	const handleRowActivate = (item: PreordersTableRow): void => {
		openPreorderDetails(item.preorderId, PREORDER_DETAILS_TAB.DETAILS);
	};

	const columns: TableColumn<PreordersTableRow>[] = [
		{
			id: PREORDERS_TABLE_COLUMN_ID.BUYER,
			title: 'Buyer',
			sortable: true,
			sortDirection: getSortDirection(PREORDERS_TABLE_COLUMN_ID.BUYER),
			onSort: (direction) => handleSort(PREORDERS_TABLE_COLUMN_ID.BUYER, direction),
			renderCell: (item) => renderCell(PREORDERS_TABLE_COLUMN_ID.BUYER, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.STATUS,
			title: 'Status',
			sortable: false,
			renderCell: (item) => renderCell(PREORDERS_TABLE_COLUMN_ID.STATUS, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.VEHICLE,
			title: 'Vehicle',
			sortable: false,
			isButtonCell: true,
			renderCell: (item) => renderCell(PREORDERS_TABLE_COLUMN_ID.VEHICLE, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.ACCESSORIES,
			title: 'PG&A',
			sortable: false,
			isButtonCell: true,
			renderCell: (item) => renderCell(PREORDERS_TABLE_COLUMN_ID.ACCESSORIES, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT,
			title: 'Deposit amount',
			sortable: true,
			sortDirection: getSortDirection(PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT),
			onSort: (direction) => handleSort(PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT, direction),
			renderCell: (item) =>
				renderCell(PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.EST_RETAIL_PRICE,
			title: 'Est. retail price',
			sortable: false,
			renderCell: (item) =>
				renderCell(PREORDERS_TABLE_COLUMN_ID.EST_RETAIL_PRICE, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.STATE,
			title: 'State',
			sortable: false,
			renderCell: (item) => renderCell(PREORDERS_TABLE_COLUMN_ID.STATE, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED,
			title: 'Deposit date',
			sortable: true,
			sortDirection: getSortDirection(PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED),
			onSort: (direction) => handleSort(PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED, direction),
			renderCell: (item) =>
				renderCell(PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED, item, isLoading),
		},
		{
			id: PREORDERS_TABLE_COLUMN_ID.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) =>
				isLoading ? (
					<SkeletonRect width={100} height={24} />
				) : (
					<PreorderActions item={item} currentTab={currentTab} context={PREORDER_ACTIONS_CONTEXT.TABLE_ROW} />
				),
			renderHeader: () => null,
		},
	];

	async function handleExportSelectedAsCsv() {
		setLoadingExport(true);
		let preorderIds: string[] | undefined;

		if (isAllMatchingSelected) {
			preorderIds = undefined;
		} else if (selectedPreorders === 'all') {
			preorderIds = items.map((item) => item.preorderId);
		} else if (selectedPreorders instanceof Set) {
			preorderIds = Array.from(selectedPreorders).map((id) => String(id));
		}

		try {
			await exportPreordersMutation.mutateAsync({
				preorderIds,
			});

			toast({
				message: `${selectedPreordersCount} preorders exported`,
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export preorders. Please try again.',
				variant: 'error',
			});
		}

		setLoadingExport(false);
	}

	const getSelectedPreorderIds = (): string[] => {
		if (selectedPreorders === 'all') {
			return items.map((item) => item.preorderId);
		}
		if (selectedPreorders instanceof Set) {
			return Array.from(selectedPreorders).map((id) => String(id));
		}
		return [];
	};

	const handleBulkStatusUpdate = (status: PreorderExternalStatus): void => {
		const preorderIds = getSelectedPreorderIds();
		if (preorderIds.length === 0) return;
		bulkStatusMutation.mutate(
			{ preorderIds, status },
			{ onSuccess: () => updateSelectedPreorders(new Set()) },
		);
	};

	// Placeholder for skeleton items when loading
	const getDisplayItems = (): PreordersTableRow[] => {
		if (isLoading) {
			return Array.from({ length: itemsPerPage }, (_, index) => ({
				preorderId: `skeleton-${index}`,
				buyerFullName: '',
				status: '',
				vehicle: { name: '' },
				accessories: [],
				state: '',
				preorderAmount: '',
				datePurchased: '',
				numberOfTasks: 0,
			}));
		}

		return items;
	};

	// Check if we have empty results (not loading, no data)
	const hasEmptyResults = !isLoading && items.length === 0;

	// Show empty state when there are no results
	if (hasEmptyResults) {
		return (
			<Empty
				icon={<NoOrders />}
				title={hasFiltersApplied ? 'No preorders match your filters' : 'No preorders yet'}
				description={
					hasFiltersApplied
						? 'Try adjusting or clearing your filters to see more results.'
						: "When you start receiving preorders, they'll show up here."
				}
				content={
					hasFiltersApplied ? (
						<Button variant='secondary' size='small' onPress={() => clearAllFilters()}>
							Clear all
						</Button>
					) : undefined
				}
			/>
		);
	}

	return (
		<>
			<Table<PreordersTableRow>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.preorderId}
				selectionMode='multiple'
				selectedItems={selectedPreorders}
				onSelectionChange={updateSelectedPreorders}
				onRowActivate={handleRowActivate}
				aria-label={isLoading ? 'Loading preorders table' : 'Preorders table'}
				stickyRightCount={1}
				belowTableContent={
					!hasEmptyResults &&
					!isLoading && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Preorders table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedPreordersCount > 0}
				onClose={() => {
					updateSelectedPreorders(new Set());
				}}
				closeButtonTooltip='Deselect all'
				leftContent={
					<SelectAllBanner
						selectedCount={
							isAllPageSelected
								? items.length
								: (selectedPreorders as Set<string>).size
						}
						totalCount={queryCount}
						isAllPageSelected={isAllPageSelected}
						isAllMatchingSelected={isAllMatchingSelected}
						onSelectAllMatching={() => setIsAllMatchingSelected(true)}
						onClearSelection={() => updateSelectedPreorders(new Set())}
						resourceName='preorders'
					/>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						{currentTab === PREORDERS_TABLE_TAB.PENDING_INVITATION && (
							<PopupTrigger>
								<Button
									size='medium'
									variant='filled'
									rounding='10'
									isLoading={bulkInviteMutation.isPending}>
									Invite to checkout
								</Button>
								<Popup title='Invite to checkout' hasCloseButton>
									{(close) => (
										<Stack itemsSpacing='24'>
											<Text size='body' color='secondary'>
												{selectedPreordersCount === 1
													? <>Are you sure you want to invite{' '}
														<Text tag='span' weight='bold'>
															{(() => {
																const selectedId = selectedPreorders instanceof Set
																	? Array.from(selectedPreorders)[0]
																	: undefined;
																const match = selectedId
																	? items.find((i) => i.preorderId === selectedId)
																	: undefined;
																return match?.buyerFullName ?? 'this buyer';
															})()}
														</Text>
														{"'s"} preorder to checkout?</>
													: <>Are you sure you want to invite{' '}
														{selectedPreordersCount} preorders to
														checkout?</>}
											</Text>
											<Group itemsAlignX='end' itemsSpacing='12'>
												<Button
													variant='secondary'
													size='medium'
													isDisabled={bulkInviteMutation.isPending}
													onPress={close}>
													Cancel
												</Button>
												<Button
													variant='filled'
													size='medium'
													isLoading={bulkInviteMutation.isPending}
													onPress={() => {
														const preorderIds =
															getSelectedPreorderIds();
														if (preorderIds.length === 0) return;
														const buyerFullName =
															preorderIds.length === 1
																? items.find(
																		(i) =>
																			i.preorderId ===
																			preorderIds[0],
																	)?.buyerFullName
																: undefined;
														bulkInviteMutation.mutate(
															{ preorderIds, buyerFullName },
															{
																onSettled: () => {
																	close();
																	updateSelectedPreorders(
																		new Set(),
																	);
																},
															},
														);
													}}>
													Invite
												</Button>
											</Group>
										</Stack>
									)}
								</Popup>
							</PopupTrigger>
						)}
						{currentTab === PREORDERS_TABLE_TAB.AWAITING_CHECKOUT && (
							<Button
								size='medium'
								variant='filled'
								rounding='10'
								isLoading={bulkStatusMutation.isPending}
								onPress={() =>
									handleBulkStatusUpdate(PreorderExternalStatus.IDLE)
								}>
								Mark as idle
							</Button>
						)}
						{currentTab === PREORDERS_TABLE_TAB.IDLE && (
							<Button
								size='medium'
								variant='filled'
								rounding='10'
								isLoading={bulkStatusMutation.isPending}
								onPress={() =>
									handleBulkStatusUpdate(
										PreorderExternalStatus.AWAITING_CHECKOUT,
									)
								}>
								Mark as active
							</Button>
						)}
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							isLoading={loadingExport}
							onPress={handleExportSelectedAsCsv}>
							Export
						</Button>
					</Group>
				}
			/>
			<PreorderDetailsDrawer />
		</>
	);
};
